// Camera + upload abstraction.
//
// Two entry points:
//   - capturePhoto() — open the system camera (rear camera on a phone).
//   - uploadPhoto()  — open the gallery / Files picker so the user can choose an existing
//                      image. Useful for scanned worksheets, screenshots, or worksheets
//                      that arrived as an email attachment.
//
// Native shells (Capacitor on iOS/Android) get the proper system picker via the Camera plugin.
// Web falls back to a hidden <input type="file"> with or without `capture` depending on intent.
//
// Both functions return a data URL on success, or null if the user cancelled.

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

function isCapacitorNative(): boolean {
  // Capacitor sets a global at runtime when running inside the native shell.
  // Outside of the shell we fall back to the web input.
  return typeof (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform === 'function'
    && (window as unknown as { Capacitor: { isNativePlatform: () => boolean } }).Capacitor.isNativePlatform();
}

export async function capturePhoto(): Promise<string | null> {
  if (isCapacitorNative()) {
    return captureNative(CameraSource.Camera);
  }
  return pickFromFileInput({ camera: true });
}

export async function uploadPhoto(): Promise<string | null> {
  if (isCapacitorNative()) {
    return captureNative(CameraSource.Photos);
  }
  return pickFromFileInput({ camera: false });
}

async function captureNative(source: CameraSource): Promise<string | null> {
  try {
    const result = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source,
      correctOrientation: true,
      presentationStyle: 'fullscreen',
    });
    return result.dataUrl ?? null;
  } catch (e) {
    const msg = (e as Error)?.message ?? '';
    // Capacitor throws when the user cancels. Treat as "no photo", not as an error.
    if (msg.includes('cancel')) return null;
    throw e;
  }
}

// Web file-input. When `camera` is true we set capture="environment" so phones jump
// straight to the rear camera. When false we OMIT the capture attribute so the
// browser shows its native picker (gallery + Files on iOS, Photos + Files on Android,
// the OS file dialog on desktop). The user can then pick an existing image, including
// scans and email attachments saved to Files.
function pickFromFileInput({ camera }: { camera: boolean }): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (camera) input.setAttribute('capture', 'environment');
    input.style.display = 'none';

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        document.body.removeChild(input);
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        document.body.removeChild(input);
        resolve(typeof reader.result === 'string' ? reader.result : null);
      };
      reader.onerror = () => {
        document.body.removeChild(input);
        reject(reader.error);
      };
      reader.readAsDataURL(file);
    };

    // Some browsers fire neither onchange nor a cancel event reliably.
    // We accept that and rely on the user retrying if needed.
    document.body.appendChild(input);
    input.click();
  });
}
